import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Users, BookOpen, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/seo/SEO';
import content from '../content.json';

const valueIcons = [Target, Users, BookOpen, Zap];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

export default function About() {
  const { title, subtitle, story, mission, values, approach } = content.about;

  return (
    <>
      <SEO 
        title={content.seo.about.title}
        description={content.seo.about.description}
        canonical="https://dakshtechinfo.uk/about"
      />
      
      <div data-testid="about-page">
        {/* Hero Section */}
        <section className="section-padding bg-muted/30" data-testid="about-hero">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="max-w-3xl mx-auto text-center"
            >
              <motion.h1 
                variants={itemVariants}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
              >
                {title}
              </motion.h1>
              <motion.p 
                variants={itemVariants}
                className="text-base md:text-lg text-muted-foreground"
              >
                {subtitle}
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Story Section */}
        <section className="section-padding" data-testid="about-story">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
              >
                <motion.h2 
                  variants={itemVariants}
                  className="text-3xl md:text-4xl font-bold mb-6"
                >
                  {story.title}
                </motion.h2>
                <div className="space-y-4">
                  {story.paragraphs.map((paragraph, index) => (
                    <motion.p 
                      key={index}
                      variants={itemVariants}
                      className="text-muted-foreground"
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                  <img 
                    src={content.image_urls?.about_team?.url || 'https://images.unsplash.com/photo-1630673489068-d329fa4e2767?crop=entropy&cs=srgb&fm=jpg&q=85'}
                    alt="DakshTechInfo Team"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-accent/20 rounded-full blur-2xl" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="section-padding bg-muted/30" data-testid="about-mission">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="max-w-3xl mx-auto text-center"
            >
              <motion.div 
                variants={itemVariants}
                className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6"
              >
                <Target className="h-8 w-8 text-accent" />
              </motion.div>
              <motion.h2 
                variants={itemVariants}
                className="text-3xl md:text-4xl font-bold mb-6"
              >
                {mission.title}
              </motion.h2>
              <motion.p 
                variants={itemVariants}
                className="text-base md:text-lg text-muted-foreground"
              >
                {mission.description}
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Values Section */}
        <section className="section-padding" data-testid="about-values">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              <motion.div variants={itemVariants} className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Values</h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  The principles that guide everything we do
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {values.map((value, index) => {
                  const Icon = valueIcons[index] || Target;
                  return (
                    <motion.div key={index} variants={itemVariants}>
                      <Card 
                        className="h-full hover-lift border-border/50"
                        data-testid={`value-${index}`}
                      >
                        <CardContent className="p-6 md:p-8">
                          <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                            <Icon className="h-6 w-6 text-accent" />
                          </div>
                          <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                          <p className="text-muted-foreground">{value.description}</p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Approach Section */}
        <section className="section-padding bg-muted/30" data-testid="about-approach">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              <motion.div variants={itemVariants} className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">{approach.title}</h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  A proven methodology for delivering successful outcomes
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {approach.steps.map((step, index) => (
                  <motion.div 
                    key={index} 
                    variants={itemVariants}
                    className="relative"
                  >
                    <Card 
                      className="h-full border-border/50"
                      data-testid={`approach-step-${index}`}
                    >
                      <CardContent className="p-6">
                        <div className="text-5xl font-bold text-accent/20 mb-4">
                          {step.step}
                        </div>
                        <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                        <p className="text-sm text-muted-foreground">{step.description}</p>
                      </CardContent>
                    </Card>
                    {index < approach.steps.length - 1 && (
                      <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                        <ArrowRight className="h-6 w-6 text-muted-foreground/30" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding" data-testid="about-cta">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="text-center max-w-2xl mx-auto"
            >
              <motion.h2 
                variants={itemVariants}
                className="text-2xl md:text-3xl font-bold mb-4"
              >
                Ready to Work Together?
              </motion.h2>
              <motion.p 
                variants={itemVariants}
                className="text-muted-foreground mb-8"
              >
                Let's discuss how we can help transform your infrastructure and accelerate your business.
              </motion.p>
              <motion.div variants={itemVariants}>
                <Button 
                  size="lg" 
                  asChild
                  className="group"
                  data-testid="about-contact-cta"
                >
                  <Link to="/contact">
                    Get in Touch
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
